import { type Scalars } from '../__generated-gql-types__/globalTypes';
import { useLex } from '../context/LexContext';
import { LexSlug } from '../context/LexContext';

import {
  BASE_LELAND_PLUS_MARKETING_URL_LEARN_WORLDS,
  BASE_LELAND_PLUS_MARKETING_URL_ON_STACK,
  CAREER_CATEGORY_ID,
  COLLEGE_CATEGORY_ID,
  DEFERRED_MBA_SUBCATEGORY_SLUG,
  LELAND_PLUS_REDIRECT_URL,
  MANAGEMENT_CONSULTING_CATEGORY_ID,
  MBA_CATEGORY_ID,
  PRODUCT_MANAGEMENT_CATEGORY_ID,
} from './constants';
import { getUrlString } from './url';

const LELAND_PLUS_CATEGORIES: Array<Scalars['CategoryUrn']['output']> = [
  MBA_CATEGORY_ID,
  MANAGEMENT_CONSULTING_CATEGORY_ID,
  COLLEGE_CATEGORY_ID,
  CAREER_CATEGORY_ID,
  PRODUCT_MANAGEMENT_CATEGORY_ID,
];

export const isLiveLelandPlusCategory = (
  categoryUrn: Scalars['CategoryUrn']['output'],
): boolean => LELAND_PLUS_CATEGORIES.includes(categoryUrn);

// Custom hook to get Leland Plus landing page URL with Lex state
export const useLelandPlusLandingPageUrl = ({
  isLelandPlusUser,
  categorySlug,
  subCategorySlug,
  utmMedium,
  utmCampaign,
}: {
  isLelandPlusUser: boolean;
  categorySlug?: Possible<string>;
  subCategorySlug?: Possible<string>;
  utmMedium: string;
  utmCampaign?: string;
}) => {
  const { isLexEnabled } = useLex();
  const useOnStackUrl = isLexEnabled(LexSlug.LELAND_PLUS_ON_STACK);

  return getLelandPlusLandingPageUrl({
    isLelandPlusUser,
    categorySlug,
    subCategorySlug,
    utmMedium,
    utmCampaign,
    useOnStackUrl,
  });
};

export const getLelandPlusLandingPageUrl = ({
  isLelandPlusUser,
  categorySlug,
  subCategorySlug,
  utmMedium,
  utmCampaign,
  useOnStackUrl = false,
}: {
  isLelandPlusUser: boolean;
  categorySlug?: Possible<string>;
  subCategorySlug?: Possible<string>;
  utmMedium: string;
  utmCampaign?: string;
  useOnStackUrl?: boolean;
}) => {
  if (isLelandPlusUser) {
    return useOnStackUrl
      ? BASE_LELAND_PLUS_MARKETING_URL_ON_STACK
      : LELAND_PLUS_REDIRECT_URL;
  }

  // When on stack, use the on-stack URL without category paths
  if (useOnStackUrl) {
    return getUrlString(BASE_LELAND_PLUS_MARKETING_URL_ON_STACK, {
      utm_source: 'site',
      utm_medium: utmMedium,
      utm_campaign: utmCampaign,
    });
  }

  // When not on stack, use the Learn Worlds URL with category-specific paths
  const baseUrl = `${BASE_LELAND_PLUS_MARKETING_URL_LEARN_WORLDS}/${
    !categorySlug
      ? ''
      : `${categorySlug}${
          subCategorySlug === DEFERRED_MBA_SUBCATEGORY_SLUG
            ? `/${subCategorySlug}`
            : ''
        }`
  }`;

  return getUrlString(baseUrl, {
    utm_source: 'site',
    utm_medium: utmMedium,
    utm_campaign: utmCampaign,
  });
};
